import React, { Fragment } from "react";

import {
  faCircleInfo,
  faPlus,
  faSearch,
  faTrashAlt,
} from "@fortawesome/pro-regular-svg-icons";
import {
  Controller,
  useFieldArray,
  useFormContext,
  useFormState,
  useWatch,
} from "react-hook-form";

import { Button } from "src/base-components/Button";
import { ErrorHint } from "src/base-components/ErrorHint";
import { Input } from "src/base-components/Input";
import { PredefinedValueEditor } from "src/base-components/schemaKit/PredefinedValueEditor";
import {
  SortableField,
  SortableFieldsContext,
} from "src/base-components/schemaKit/SortableFields";
import {
  DEFAULT_STRING_LENGTH,
  getPredefinedValueUniqueValidator,
} from "src/base-components/schemaKit/validators";
import {
  CasePropertyTypeSelector,
  ENTITY_REF_PREFIX,
  EVENT_REF_PREFIX,
} from "src/cases/templates/CasePropertyTypeSelector";
import { FormStepWrapper } from "src/cases/templates/FormStepWrapper";
import { getStepById } from "src/cases/templates/formSteps";
import { CaseTemplateFormValues } from "src/cases/templates/formUtils/types";
import { generateInternalId } from "src/cases/templates/internalIdUtils";
import { useCheckFieldPermission } from "src/cases/templates/useCheckFieldPermission";
import { useIsTemplateVersionPublished } from "src/cases/templates/utils";
import { isStatusProperty } from "src/cases/utils";
import {
  CasePropertiesValue,
  EnumPropertyOutput,
  TagsProperty,
} from "src/clients/case-management";
import { EmptyState } from "src/design-system/EmptyState";
import { IconButton } from "src/design-system/Icon";
import { FEATURE_FLAGS, isFeatureFlagEnabled } from "src/router/featureFlags";
import { isLikePythonKey, unslugify } from "src/utils/pythonUtils";

// Reserved property keys that cannot be changed
const RESERVED_PROPERTY_KEYS = ["status", "entity_id"] as const;

const isReservedProperty = (key: string): boolean => {
  return RESERVED_PROPERTY_KEYS.includes(
    key as (typeof RESERVED_PROPERTY_KEYS)[number],
  );
};

// Source property key for manual case creation
const SOURCE_PROPERTY_KEY = "source";

const isSourceProperty = (key: string): boolean => {
  return (
    key === SOURCE_PROPERTY_KEY &&
    isFeatureFlagEnabled(FEATURE_FLAGS.manualCaseCreation)
  );
};

// Helper function to get field errors
const getFieldError = (
  form: ReturnType<typeof useFormContext<CaseTemplateFormValues>>,
  index: number,
  field: string,
) => {
  const error =
    form.formState.errors.attributes?.case_properties?.[index]?.[
      field as keyof CasePropertiesValue
    ];
  // Return only FieldError objects that have a message property
  return error && typeof error === "object" && "message" in error
    ? error
    : undefined;
};

export const DefineCasePropertiesStep: React.FC = () => {
  const step = getStepById("define-case-properties");
  const form = useFormContext<CaseTemplateFormValues>();
  const { fields, remove, append } = useFieldArray<
    CaseTemplateFormValues,
    "attributes.case_properties",
    "id"
  >({
    name: "attributes.case_properties",
  });

  const hasPermission = useCheckFieldPermission();

  const addProperty = () => {
    append(
      {
        key: "",
        display_name: "",
        type: "string" as const,
        _isNewlyAppended: true,
      },
      { focusName: `attributes.case_properties.${fields.length}.key` },
    );
  };

  return (
    <FormStepWrapper
      description={step.description}
      docLink={step.docLink}
      title={step.title}
    >
      {fields.length > 0 ? (
        <>
          <div className="grid grid-cols-8 gap-x-2 gap-y-3">
            <CasePropertyRow
              displayName={
                <div className="font-inter-normal-12px text-gray-500">
                  Display name
                </div>
              }
              propertyName={
                <div className="font-inter-normal-12px text-gray-500">Name</div>
              }
              propertyType={
                <div className="font-inter-normal-12px text-gray-500">Type</div>
              }
            />
            {fields.map((field, index) => (
              <CasePropertyField
                key={field.id}
                field={field}
                index={index}
                remove={remove}
              />
            ))}
          </div>
          {hasPermission("add", "attributes.case_properties") && (
            <div className="mt-4">
              <Button
                dataLoc="add-property-button"
                iconLeft={faPlus}
                size="sm"
                variant="secondary"
                onClick={addProperty}
              >
                Add property
              </Button>
            </div>
          )}
        </>
      ) : (
        <div className="h-110">
          <EmptyState
            action={
              hasPermission("add", "attributes.case_properties") ? (
                <Button
                  iconLeft={faPlus}
                  size="sm"
                  variant="secondary"
                  onClick={addProperty}
                >
                  Add property
                </Button>
              ) : undefined
            }
            dataLoc="define-case-properties-empty-state"
            description="Define the properties of cases created using this template"
            headline="No case properties yet"
            icon={faSearch}
          />
        </div>
      )}
    </FormStepWrapper>
  );
};

const CasePropertyRow: React.FC<{
  propertyName: React.ReactNode;
  displayName: React.ReactNode;
  propertyType: React.ReactNode;
}> = ({ propertyName, displayName, propertyType }) => {
  return (
    <>
      <div className="col-span-2 lg:col-span-3">{propertyName}</div>
      <div className="col-span-3 lg:col-span-3">{displayName}</div>
      <div className="col-span-3 lg:col-span-2">{propertyType}</div>
    </>
  );
};

const switchToSourceEnum = (
  form: ReturnType<typeof useFormContext<CaseTemplateFormValues>>,
  index: number,
) => {
  const type = form.getValues(`attributes.case_properties.${index}.type`);
  if (type === "enum") return;

  form.setValue(`attributes.case_properties.${index}.type`, "enum");

  const currentValues = form.getValues(
    `attributes.case_properties.${index}.values`,
  );
  if (!currentValues || currentValues.length === 0) {
    form.setValue(`attributes.case_properties.${index}.values`, [
      {
        value: "decision_flow",
        display_name: "Decision Flow",
        color: "gray",
        _internalId: generateInternalId(),
      },
    ]);
  }

  form.unregister(`attributes.case_properties.${index}.entity_type`);
  form.unregister(`attributes.case_properties.${index}.event_type`);
};

const CasePropertyField: React.FC<{
  field: { id: string; key: string; _isNewlyAppended?: boolean };
  index: number;
  remove: (index: number) => void;
}> = ({ field, index, remove }) => {
  const form = useFormContext<CaseTemplateFormValues>();
  const hasPermission = useCheckFieldPermission();

  const key = useWatch<CaseTemplateFormValues>({
    name: `attributes.case_properties.${index}.key`,
  });
  const isSource = isSourceProperty(key as string);

  const isReserved = isReservedProperty(field.key);
  const isNewlyAppended = isNewlyAppendedValue(field);

  const isKeyFieldDisabled =
    !isNewlyAppended &&
    (isReserved ||
      !hasPermission("edit", `attributes.case_properties.${index}.key`));

  const isTypeFieldDisabled =
    isSource ||
    (!isNewlyAppended &&
      (isReserved ||
        !hasPermission("edit", `attributes.case_properties.${index}.type`)));

  return (
    <Fragment>
      <CasePropertyRow
        displayName={
          <div className="relative">
            <Input
              data-loc={`version-property-${index}-display-name`}
              disabled={
                !hasPermission(
                  "edit",
                  `attributes.case_properties.${index}.display_name`,
                )
              }
              errored={!!getFieldError(form, index, "display_name")}
              placeholder="Display name"
              {...form.register(
                `attributes.case_properties.${index}.display_name`,
              )}
              fullWidth
            />
            {getFieldError(form, index, "display_name") && (
              <ErrorHint>
                {getFieldError(form, index, "display_name")?.message}
              </ErrorHint>
            )}
          </div>
        }
        propertyName={
          <div className="relative">
            <Input
              data-loc={`version-property-${index}-key`}
              errored={!!getFieldError(form, index, "key")}
              placeholder="Property name"
              monospaced
              suffixIcon={
                isSource
                  ? {
                      icon: faCircleInfo,
                      helpText:
                        "Sets the Case origin during manual Case creation and can be optionally mapped in the Case creation node. If removed or changed, it won't appear in either.",
                    }
                  : undefined
              }
              {...form.register(`attributes.case_properties.${index}.key`, {
                onChange: (e) => {
                  const { isTouched } = form.getFieldState(
                    `attributes.case_properties.${index}.display_name`,
                  );

                  if (!isTouched && isNewlyAppended) {
                    const displayName = unslugify(e.target.value);
                    form.setValue(
                      `attributes.case_properties.${index}.display_name`,
                      displayName,
                      {
                        shouldTouch: false,
                      },
                    );
                  }

                  if (isSourceProperty(e.target.value)) {
                    switchToSourceEnum(form, index);
                  }
                },
              })}
              disabled={isKeyFieldDisabled}
              fullWidth
            />
            {getFieldError(form, index, "key") && (
              <ErrorHint>
                {getFieldError(form, index, "key")?.message}
              </ErrorHint>
            )}
          </div>
        }
        propertyType={
          <div className="flex items-center gap-2">
            <div className="min-w-0 flex-1">
              <Controller
                name={`attributes.case_properties.${index}.type`}
                render={({ field: controllerField }) => {
                  const currentProperty = form.getValues(
                    `attributes.case_properties.${index}`,
                  );

                  return (
                    <CasePropertyTypeSelector
                      dataLoc={`version-property-${index}-type-selector`}
                      disabled={isTypeFieldDisabled}
                      value={getPropertyTypeSelectorValue(currentProperty)}
                      onChange={(value) => {
                        const [type, entityOrEventType] =
                          parsePropertyTypeSelectorValue(value);
                        controllerField.onChange(type);

                        // If Entity Ref then set the entity type
                        if (type === "entity_ref" && entityOrEventType) {
                          form.setValue(
                            `attributes.case_properties.${index}.entity_type`,
                            entityOrEventType,
                          );
                        } else {
                          // If not Entity Ref then unregister the entity type field
                          form.unregister(
                            `attributes.case_properties.${index}.entity_type`,
                          );
                        }

                        // If Event Ref then set the event type
                        if (type === "event_ref" && entityOrEventType) {
                          form.setValue(
                            `attributes.case_properties.${index}.event_type`,
                            entityOrEventType,
                          );
                        } else {
                          // If not Event Ref then unregister the event type field
                          form.unregister(
                            `attributes.case_properties.${index}.event_type`,
                          );
                        }

                        if (type === "enum" || type === "tags") {
                          const values = form.getValues(
                            `attributes.case_properties.${index}.values`,
                          );
                          if (!values || values.length === 0) {
                            form.setValue(
                              `attributes.case_properties.${index}.values`,
                              [
                                {
                                  value: "",
                                  color: "gray",
                                  _internalId: generateInternalId(),
                                },
                              ],
                            );
                          }
                        } else {
                          form.unregister(
                            `attributes.case_properties.${index}.values`,
                          );
                        }
                      }}
                    />
                  );
                }}
              />
            </div>
            {(isNewlyAppended ||
              (hasPermission("delete", "attributes.case_properties") &&
                !isReserved)) && (
              <IconButton
                icon={faTrashAlt}
                size="sm"
                onClick={() => remove(index)}
              />
            )}
          </div>
        }
      />
      <PredefinedValuesPropertyWrapper index={index} />
    </Fragment>
  );
};

const isPredefinedValueProperty = (
  property: CasePropertiesValue,
): property is (TagsProperty | EnumPropertyOutput) & { key: string } => {
  return property.type === "enum" || property.type === "tags";
};

const PredefinedValuesPropertyWrapper: React.FC<{
  index: number;
}> = ({ index }) => {
  const property = useWatch({
    name: `attributes.case_properties.${index}`,
  });

  if (!property || !isPredefinedValueProperty(property)) {
    return null;
  }

  return <PredefinedValuesProperty index={index} property={property} />;
};

const PredefinedValuesProperty: React.FC<{
  index: number;
  property: (TagsProperty | EnumPropertyOutput) & { key: string };
}> = ({ index, property }) => {
  const hasPermission = useCheckFieldPermission();
  const form = useFormContext<CaseTemplateFormValues>();

  const { fields, append, remove, move } = useFieldArray<
    CaseTemplateFormValues,
    `attributes.case_properties.${number}.values`,
    "id"
  >({
    name: `attributes.case_properties.${index}.values`,
  });

  const formState = useFormState<CaseTemplateFormValues>();

  return (
    <div className="col-span-8 flex flex-col gap-3 border-gray-200 border-l pl-4">
      <SortableFieldsContext
        disabled={
          !hasPermission("sort", `attributes.case_properties.${index}.values`)
        }
        items={fields.map((field) => field.id)}
        onChangeOrder={({ oldIndex, newIndex }) => {
          move(oldIndex, newIndex);
        }}
      >
        {fields.map((predefinedValue, valIndex) => {
          const isNewlyAppended = isNewlyAppendedValue(predefinedValue);
          const isSystemValue =
            isStatusProperty(property) &&
            ["open", "closed"].includes(predefinedValue.value);

          const colorDisabled =
            !isNewlyAppended &&
            !hasPermission(
              "edit",
              `attributes.case_properties.${index}.values.${valIndex}.color`,
            );

          const valueDisabled =
            (isSystemValue && !isNewlyAppended) ||
            (!isNewlyAppended &&
              !hasPermission(
                "edit",
                `attributes.case_properties.${index}.values.${valIndex}.value`,
              ));

          const isValueDeleteable =
            !isSystemValue &&
            (isNewlyAppended ||
              hasPermission(
                "delete",
                `attributes.case_properties.${index}.values`,
              ));

          const displayNameDisabled =
            !isNewlyAppended &&
            !hasPermission(
              "edit",
              `attributes.case_properties.${index}.values.${valIndex}.display_name`,
            );

          const disabledFields = [
            colorDisabled && "color",
            valueDisabled && "value",
          ].filter(Boolean) as ("color" | "value")[];
          return (
            <SortableField key={predefinedValue.id} id={predefinedValue.id}>
              <div className="flex flex-col gap-3">
                <div className="flex items-start gap-2">
                  <div className="flex-1">
                    <PredefinedValueEditor
                      colorField={{
                        name: `attributes.case_properties.${index}.values.${valIndex}.color`,
                      }}
                      dataLoc={`version-property-${index}-${valIndex}`}
                      disabled={disabledFields}
                      error={getPredefinedValueError(
                        formState,
                        index,
                        valIndex,
                      )}
                      valueField={{
                        name: `attributes.case_properties.${index}.values.${valIndex}.value`,
                        rules: {
                          required: "Value is required",
                          maxLength: {
                            value: DEFAULT_STRING_LENGTH,
                            message: `Value must be less than ${DEFAULT_STRING_LENGTH} characters`,
                          },
                          validate: {
                            unique: getPredefinedValueUniqueValidator(
                              `attributes.case_properties.${index}.values`,
                            ),
                            ...(isStatusProperty(property) && {
                              pythonVariable: (value) => {
                                if (!isLikePythonKey(value)) {
                                  return "Status value must begin with a letter, and may only include letters, numbers, and underscores";
                                }
                                return true;
                              },
                            }),
                          },
                        },
                      }}
                    />
                  </div>
                  <div className="flex-1">
                    <Input
                      data-loc={`version-property-${index}-${valIndex}-display-name`}
                      disabled={displayNameDisabled}
                      errored={
                        !!getPredefinedValueObject(formState, index, valIndex)
                          ?.display_name
                      }
                      placeholder="Display name"
                      fullWidth
                      {...form.register(
                        `attributes.case_properties.${index}.values.${valIndex}.display_name`,
                        {
                          maxLength: {
                            value: DEFAULT_STRING_LENGTH,
                            message: `Display name must be less than ${DEFAULT_STRING_LENGTH} characters`,
                          },
                        },
                      )}
                    />
                    {getPredefinedValueObject(formState, index, valIndex)
                      ?.display_name && (
                      <ErrorHint>
                        {
                          getPredefinedValueObject(formState, index, valIndex)
                            ?.display_name?.message
                        }
                      </ErrorHint>
                    )}
                  </div>
                  {isValueDeleteable && (
                    <div className="mt-1">
                      <IconButton
                        disabled={fields.length <= 1}
                        icon={faTrashAlt}
                        size="sm"
                        onClick={() => remove(valIndex)}
                      />
                    </div>
                  )}
                </div>
                {isStatusProperty(property) && (
                  <PredefinedSubValuesPropertyEditor
                    index={valIndex}
                    parentPropertyIndex={index}
                  />
                )}
              </div>
            </SortableField>
          );
        })}
      </SortableFieldsContext>
      {hasPermission("add", `attributes.case_properties.${index}.values`) && (
        <div>
          <Button
            dataLoc={`add-${property.type}-value-button-${index}`}
            iconLeft={faPlus}
            size="sm"
            variant="secondary"
            onClick={() => {
              append({
                value: "",
                color: "gray",
                _isNewlyAppended: true,
                _internalId: generateInternalId(),
              });
            }}
          >
            {getButtonCopy(property)}
          </Button>
        </div>
      )}
    </div>
  );
};

const getPredefinedValueObject = (
  formState: ReturnType<typeof useFormState<CaseTemplateFormValues>>,
  index: number,
  valIndex: number,
) => {
  const propertyError = formState.errors.attributes?.case_properties?.[index];

  if (
    propertyError &&
    "values" in propertyError &&
    Array.isArray(propertyError.values)
  ) {
    return propertyError.values[valIndex];
  }

  return undefined;
};

const getPredefinedValueError = (
  formState: ReturnType<typeof useFormState<CaseTemplateFormValues>>,
  index: number,
  valIndex: number,
) => {
  return getPredefinedValueObject(formState, index, valIndex)?.value?.message;
};

const getPredefinedSubValueError = (
  formState: ReturnType<typeof useFormState<CaseTemplateFormValues>>,
  index: number,
  valIndex: number,
  subValIndex: number,
) => {
  return getPredefinedValueObject(formState, index, valIndex)?.sub_values?.[
    subValIndex
  ]?.value?.message;
};

const PredefinedSubValuesPropertyEditor: React.FC<{
  parentPropertyIndex: number;
  index: number;
}> = ({ parentPropertyIndex, index }) => {
  const isPublishedVersion = useIsTemplateVersionPublished();
  const form = useFormContext<CaseTemplateFormValues>();
  const { fields, append, remove, move } = useFieldArray({
    name: `attributes.case_properties.${parentPropertyIndex}.values.${index}.sub_values`,
  });
  const formState = useFormState<CaseTemplateFormValues>();
  const hasPermission = useCheckFieldPermission();

  if (isPublishedVersion && fields.length === 0) {
    return null;
  }

  return (
    <div className="col-span-8 flex flex-col gap-3 pl-20">
      <SortableFieldsContext
        disabled={
          !hasPermission(
            "sort",
            `attributes.case_properties.${parentPropertyIndex}.values.${index}.sub_values`,
          )
        }
        items={fields.map((field) => field.id)}
        onChangeOrder={({ oldIndex, newIndex }) => {
          move(oldIndex, newIndex);
        }}
      >
        {fields.map((predefinedValue, valIndex) => {
          const isNewlyAppended = isNewlyAppendedValue(predefinedValue);

          return (
            <SortableField key={predefinedValue.id} id={predefinedValue.id}>
              <div>
                <div className="flex items-start gap-2">
                  <div className="flex-1">
                    <PredefinedValueEditor
                      disabled={
                        !isNewlyAppended &&
                        !hasPermission(
                          "edit",
                          `attributes.case_properties.${parentPropertyIndex}.values.${index}.sub_values.${valIndex}.value`,
                        )
                      }
                      error={getPredefinedSubValueError(
                        formState,
                        parentPropertyIndex,
                        index,
                        valIndex,
                      )}
                      valueField={{
                        name: `attributes.case_properties.${parentPropertyIndex}.values.${index}.sub_values.${valIndex}.value`,
                        rules: {
                          required: "Value is required",
                          maxLength: {
                            value: DEFAULT_STRING_LENGTH,
                            message: `Value must be less than ${DEFAULT_STRING_LENGTH} characters`,
                          },
                          validate: {
                            unique: getPredefinedValueUniqueValidator(
                              `attributes.case_properties.${parentPropertyIndex}.values.${index}.sub_values`,
                            ),
                            pythonVariable: (value) => {
                              if (!isLikePythonKey(value)) {
                                return "Sub-status value must begin with a letter, and may only include letters, numbers, and underscores";
                              }
                              return true;
                            },
                          },
                        },
                      }}
                      hideColorSelector
                    />
                  </div>
                  <div className="flex-1">
                    <Input
                      data-loc={`version-property-${parentPropertyIndex}-${index}-sub-${valIndex}-display-name`}
                      disabled={
                        !isNewlyAppended &&
                        !hasPermission(
                          "edit",
                          `attributes.case_properties.${parentPropertyIndex}.values.${index}.sub_values.${valIndex}.display_name`,
                        )
                      }
                      errored={
                        !!getPredefinedValueObject(
                          formState,
                          parentPropertyIndex,
                          index,
                        )?.sub_values?.[valIndex]?.display_name
                      }
                      placeholder="Display name"
                      fullWidth
                      {...form.register(
                        `attributes.case_properties.${parentPropertyIndex}.values.${index}.sub_values.${valIndex}.display_name`,
                        {
                          maxLength: {
                            value: DEFAULT_STRING_LENGTH,
                            message: `Display name must be less than ${DEFAULT_STRING_LENGTH} characters`,
                          },
                        },
                      )}
                    />
                    {getPredefinedValueObject(
                      formState,
                      parentPropertyIndex,
                      index,
                    )?.sub_values?.[valIndex]?.display_name && (
                      <ErrorHint>
                        {
                          getPredefinedValueObject(
                            formState,
                            parentPropertyIndex,
                            index,
                          )?.sub_values?.[valIndex]?.display_name?.message
                        }
                      </ErrorHint>
                    )}
                  </div>
                  {(isNewlyAppended ||
                    hasPermission(
                      "delete",
                      `attributes.case_properties.${parentPropertyIndex}.values.${index}.sub_values`,
                    )) && (
                    <div className="mt-1">
                      <IconButton
                        icon={faTrashAlt}
                        size="sm"
                        onClick={() => remove(valIndex)}
                      />
                    </div>
                  )}
                </div>
              </div>
            </SortableField>
          );
        })}
      </SortableFieldsContext>
      {hasPermission(
        "add",
        `attributes.case_properties.${parentPropertyIndex}.values.${index}.sub_values`,
      ) && (
        <div>
          <Button
            iconLeft={faPlus}
            size="sm"
            variant="secondary"
            onClick={() => {
              append({
                value: "",
                color: "gray",
                _isNewlyAppended: true,
                _internalId: generateInternalId(),
              });
            }}
          >
            Add sub-status
          </Button>
        </div>
      )}
    </div>
  );
};

const getButtonCopy = (
  property: (TagsProperty | EnumPropertyOutput) & { key: string },
) => {
  if (property.type === "enum" && property.key === "status") {
    return "Add status";
  }

  if (property.type === "tags") {
    return "Add tag";
  }

  return "Add value";
};

const isNewlyAppendedValue = (value: object) => {
  return "_isNewlyAppended" in value;
};

const getPropertyTypeSelectorValue = (property: CasePropertiesValue) => {
  if (property.type === "entity_ref") {
    return `${ENTITY_REF_PREFIX}${property.entity_type}`;
  }

  if (property.type === "event_ref") {
    return `${EVENT_REF_PREFIX}${property.event_type}`;
  }

  return property.type;
};

const parsePropertyTypeSelectorValue = (value: string) => {
  if (value.startsWith(ENTITY_REF_PREFIX)) {
    return ["entity_ref", value.replace(ENTITY_REF_PREFIX, "")] as const;
  }

  if (value.startsWith(EVENT_REF_PREFIX)) {
    return ["event_ref", value.replace(EVENT_REF_PREFIX, "")] as const;
  }

  return [value] as const;
};
